import { memo, useEffect } from "react";
import {
  Button,
  Flex,
  Icon,
  Link,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@repo/ping-design";
import { GeocoderType, LatLngType } from "../../ts-types/mapsTypes";
import {
  GET_SERVICE_COLOR,
  GET_SERVICE_DESCRIPTION,
} from "../../constants/maps-constants";
import {
  calculateAngleBetweenPoints,
  distanceBetweenTwoPointsVerbose,
} from "../../utils/lat-lng-utils";
import "./GeocoderLegendItem.scss";

const GeocodingConfidenceIcon = ({
  confidence,
}: {
  confidence?: number | string;
}) => {
  const numConfidence =
    typeof confidence === "number"
      ? confidence
      : confidence === "Not Applicable"
        ? -1
        : 100;

  if (numConfidence < 0) {
    return <Icon iconName="CircleHelp" size={16} />;
  } else if (numConfidence >= 80) {
    return <Icon iconName="AccuracyVeryHighPing" size={16} />;
  } else if (numConfidence >= 60) {
    return <Icon iconName="AccuracyHighPing" size={16} />;
  } else if (numConfidence >= 40) {
    return <Icon iconName="AccuracyMediumPing" size={16} />;
  } else {
    return <Icon iconName="AccuracyLowPing" size={16} />;
  }
};

type GeocoderLegendItemProps = {
  source: GeocoderType;
  center?: LatLngType;
  hoveredMarker?: string | null;
  relativeTo?: LatLngType;
  onClick?: (serviceId: string) => void;
  onMouseOver?: (serviceId: string | null) => void;
  onClickName?: (serviceId: string) => void;
  classNamePrefix?: string;
};

export const GeocoderLegendItem = memo(
  ({
    source,
    center,
    hoveredMarker,
    relativeTo,
    onClick,
    onMouseOver,
    onClickName,
    classNamePrefix = "GeocoderLegendItem",
  }: GeocoderLegendItemProps) => {
    let angle = 0;
    let distance = "";

    if (source?.lat && relativeTo?.lat && relativeTo?.lng) {
      angle = calculateAngleBetweenPoints(
        relativeTo.lat,
        relativeTo.lng,
        source.lat,
        source.lng,
      );
      distance = distanceBetweenTwoPointsVerbose(
        source.lat,
        source.lng,
        relativeTo.lat,
        relativeTo.lng,
      );
    } else if (source?.lat && center?.lat && center?.lng) {
      angle = calculateAngleBetweenPoints(
        center.lat,
        center.lng,
        source.lat,
        source.lng,
      );
      distance = distanceBetweenTwoPointsVerbose(
        source.lat,
        source.lng,
        center.lat,
        center.lng,
      );
    }

    useEffect(() => {
      if (hoveredMarker === null) {
        onMouseOver?.(null);
      }
    }, [hoveredMarker, onMouseOver]);

    const description = GET_SERVICE_DESCRIPTION(source);
    if (!description) {
      return null;
    }

    return (
      <Flex direction="row" justify="space-between">
        <Flex className={`${classNamePrefix}__Left`} gap="2">
          <div
            className={`${classNamePrefix}__Left__Color`}
            style={{
              backgroundColor: GET_SERVICE_COLOR(source?.source),
            }}
          />
          <Flex direction="column">
            {onClickName ? (
              <Link
                className={`${classNamePrefix}__Left__ServiceName`}
                onClick={() => {
                  onClickName?.(source?.source);
                }}
                onMouseOver={() => {
                  onMouseOver?.(source?.source);
                }}
                onMouseOut={() => {
                  onMouseOver?.(null);
                }}
              >
                {description || "No Name"}
              </Link>
            ) : (
              <span className={`${classNamePrefix}__Left__ServiceName`}>
                {description || "No Name"}
              </span>
            )}

            <Flex direction="row" gap="1">
              <span className={`${classNamePrefix}__Left__Accuracy`}>
                Accuracy
              </span>
              <GeocodingConfidenceIcon confidence={source?.confidence} />
            </Flex>
          </Flex>
        </Flex>
        <Flex
          className={`${classNamePrefix}__Right`}
          gap="5"
          align="center"
          justify="space-between"
        >
          <Tooltip placement="bottom">
            <TooltipTrigger>
              <Flex
                className={`${classNamePrefix}__Right__Distance`}
                align="center"
                gap="1"
              >
                <Icon
                  iconName="ArrowUp"
                  size={24}
                  style={{
                    transform: `rotate(${angle}deg)`,
                    transformOrigin: "center",
                  }}
                />
                <span className={`${classNamePrefix}__Right__Distance__Text`}>
                  {distance}
                </span>
              </Flex>
            </TooltipTrigger>
            <TooltipContent>
              Distance relative to{" "}
              {relativeTo ? "Ping Geocoding" : "Center of the map"}
            </TooltipContent>
          </Tooltip>
          <Tooltip placement="right">
            <TooltipTrigger asChild>
              <Button
                iconName="LocateFixed"
                size="sm"
                variant="outline"
                onClick={() => {
                  onClick?.(source?.source);
                }}
                onMouseOver={() => {
                  onMouseOver?.(source?.source);
                }}
                onMouseOut={() => {
                  onMouseOver?.(null);
                }}
              />
            </TooltipTrigger>
            <TooltipContent>Jump to location</TooltipContent>
          </Tooltip>
        </Flex>
      </Flex>
    );
  },
);

GeocoderLegendItem.displayName = "GeocoderLegendItem";
